import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useContact } from '../../hooks/queries/useContacts';
import {
  formatEuros,
  type BillingDocument,
  type BillingLineItem,
} from '../../services/billing/billingService';
import { createPolarClientDeps } from '../../services/billing/polarBilling';
import {
  getPolarPaperLogo,
  getPolarPaperSettings,
  type PolarIssuerProfile,
  type PolarPapier,
} from '../../services/billing/polarClient';
import { polarDocumentNumber } from '../../services/billing/polarDisplay';
import { computeLineTotals, formatVatRate } from '../../lib/billing/lineTotals';
import './QuotePreview.css';

interface Props {
  doc: BillingDocument;
  lines: BillingLineItem[];
  dossierLabel?: string | null;
}

/** Wordmark vert (#043429). Le logo actif du cabinet est blanc, prévu pour la sidebar sombre. */
const CABINET_MARK = '/cime-logo-loader.svg';
const CABINET_MARK_MM = 46;

const PAYMENT_TERMS = [
  { label: 'Délai de paiement', value: 'À la réception' },
  { label: 'Pénalité de retard', value: '3 fois le taux légal' },
  { label: 'Indemnité forfaitaire pour frais de recouvrement', value: '40 €' },
  { label: 'Escompte', value: 'Escompte pour paiement anticipé : néant' },
  { label: 'Moyens de paiement', value: 'Virement, Chèque' },
];

function present(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function hexOr(value: string | undefined, fallback: string): string {
  const s = String(value ?? '').trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(s) ? s : fallback;
}

function inkOnFill(fill: string): string {
  const n = fill.replace('#', '');
  if (n.length !== 6) return '#ffffff';
  const channel = (hex: string) => {
    const c = parseInt(hex, 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * channel(n.slice(0, 2)) + 0.7152 * channel(n.slice(2, 4)) + 0.0722 * channel(n.slice(4, 6));
  return L > 0.55 ? '#273043' : '#ffffff';
}

function paperStyle(papier: PolarPapier | undefined): CSSProperties {
  const encre = hexOr(papier?.encre, '#273043');
  const accent = hexOr(papier?.accent, '#d62246');
  const bandeau = hexOr(papier?.bandeau, encre);
  const barre = hexOr(papier?.barre_ttc, encre);
  return {
    ['--paper-encre' as string]: encre,
    ['--paper-accent' as string]: accent,
    ['--paper-bandeau' as string]: bandeau,
    ['--paper-barre' as string]: barre,
    ['--paper-on-bandeau' as string]: inkOnFill(bandeau),
    ['--paper-on-barre' as string]: inkOnFill(barre),
  };
}

function frDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function formatIban(iban: string): string {
  return iban.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
}

function legalMentions(issuer: PolarIssuerProfile | undefined): string[] {
  if (!issuer) return [];
  const form = (issuer.legal_form ?? '').trim();
  const name = issuer.legal_name ?? '';
  const sole = /\b(ei|eirl|entreprise individuelle)\b/i.test(`${form} ${name}`);
  const lines: string[] = [];
  const capital = Number(issuer.capital_cents ?? 0);
  if (!sole && form) {
    lines.push(capital > 0 ? `${form} au capital de ${formatEuros(capital / 100)}` : form);
  }
  if (present(issuer.rcs)) {
    lines.push(/^rcs\b/i.test(issuer.rcs) ? issuer.rcs : `RCS : ${issuer.rcs}`);
  }
  return lines;
}

function dueDays(issue: string | null, due: string | null): number | null {
  if (!issue || !due) return null;
  const a = Date.parse(`${issue.slice(0, 10)}T00:00:00`);
  const b = Date.parse(`${due.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

export function BillingSheet({ doc, lines, dossierLabel }: Props) {
  const contact = useContact(doc.client_contact_id ?? undefined);
  const paper = useQuery({
    queryKey: ['polar', 'paper-settings'],
    queryFn: () => getPolarPaperSettings(createPolarClientDeps()),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const issuer = paper.data?.issuer;
  const papier = paper.data?.papier;
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    getPolarPaperLogo(createPolarClientDeps())
      .then((blob) => {
        if (!blob || cancelled || !blob.type.startsWith('image/')) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      })
      .catch(() => setLogoUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const kind = doc.doc_type === 'estimate' ? 'quote' : doc.doc_type === 'credit_note' ? 'credit' : 'invoice';
  const title = kind === 'credit' ? 'Avoir' : kind === 'quote' ? 'Devis' : 'Facture';
  const number = polarDocumentNumber(doc) ?? 'Brouillon';
  const client = contact.data;
  const clientName = client?.company_name?.trim()
    || [client?.first_name, client?.last_name].filter(Boolean).join(' ').trim()
    || '';
  const person = [client?.first_name, client?.last_name].filter(Boolean).join(' ').trim();
  const b2c = !!client && !present(client.company_name);
  const terms = (b2c
    ? PAYMENT_TERMS.filter((t) => t.label !== 'Pénalité de retard' && t.label !== 'Indemnité forfaitaire pour frais de recouvrement')
    : PAYMENT_TERMS
  ).map((t) => {
    if (t.label === 'Escompte' && present(issuer?.discount_mention)) return { ...t, value: issuer.discount_mention };
    if (t.label === 'Pénalité de retard' && present(issuer?.penalty_mention)) return { ...t, value: issuer.penalty_mention };
    if (t.label === 'Délai de paiement') {
      const days = dueDays(doc.issue_date, doc.due_date);
      if (days == null) return t;
      return { ...t, value: days <= 0 ? 'À la réception' : `${days} jours` };
    }
    return t;
  });
  if (issuer?.vat_regime === 'debits') {
    terms.push({ label: 'Régime de TVA', value: 'Option pour le paiement de la taxe d’après les débits' });
  }
  const iban = present(issuer?.iban) ? formatIban(issuer.iban) : '';
  const bic = present(issuer?.bic) ? issuer.bic.trim() : '';
  const logoMm = Number(papier?.largeur_mm);
  const logoWidth = logoUrl
    ? (Number.isFinite(logoMm) && logoMm > 0 ? logoMm : 20)
    : CABINET_MARK_MM;
  const logoSrc = logoUrl ?? CABINET_MARK;
  const vatRate = Number(doc.vat_rate);
  const byRate = computeLineTotals(lines, vatRate).byRate;

  return (
    <aside className="min-w-0 lg:sticky lg:top-4">
      <p className="mb-2 text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-neutral-500">Aperçu</p>
      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <div className="qp-sheet" style={paperStyle(papier)}>
          <article className="qp">
            <header className="qp-head">
              <img className="qp-logo" src={logoSrc} alt={issuer?.legal_name || 'Cime Avocats'} style={{ width: `${logoWidth}mm` }} />
              <div className="qp-doc">
                <div className="qp-doc-title">{title}</div>
                <div className="qp-doc-num">{number}</div>
              </div>
            </header>

            <div className="qp-parties">
              <div className="qp-issuer">
                {present(issuer?.legal_name) ? <div className="qp-name">{issuer.legal_name}</div> : null}
                {present(issuer?.address_line) ? <div>{issuer.address_line}</div> : null}
                <div>{[issuer?.postal_code, issuer?.city].filter(present).join(' ')}</div>
                {legalMentions(issuer).map((line) => <div key={line}>{line}</div>)}
                {present(issuer?.siret) ? <div>N° SIRET : {issuer.siret}</div> : null}
                {present(issuer?.vat_intra) ? <div>N° TVA : {issuer.vat_intra}</div> : null}
                <div className="qp-dates">
                  <div>
                    <span className="qp-date-label">Date d'émission</span>
                    <span>{frDate(doc.issue_date)}</span>
                  </div>
                  {kind !== 'credit' && (
                    <div>
                      <span className="qp-date-label">
                        {kind === 'quote' ? 'Date de validité' : "Date d'exigibilité du paiement"}
                      </span>
                      <span>{frDate(doc.due_date)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="qp-client">
                {clientName ? (
                  <>
                    <div className="qp-name">{clientName}</div>
                    {present(person) && person !== clientName ? <div>{person}</div> : null}
                    {present(client?.address_line1) ? <div>{client.address_line1}</div> : null}
                    <div>{[client?.postal_code, client?.city].filter(present).join(' ')}</div>
                    {present(client?.siret) ? <div>N° SIRET : {client.siret.replace(/\s+/g, '')}</div> : null}
                  </>
                ) : (
                  <div className="qp-client-ph">Client à préciser</div>
                )}
              </div>
            </div>

            {dossierLabel ? <div className="qp-ref">{dossierLabel}</div> : null}
            <div className="qp-ref">Catégorie d'opération Prestation de services</div>
            {kind !== 'credit' && present(issuer?.header_note) ? <p className="qp-cgv">{issuer.header_note}</p> : null}

            <div className="qp-body">
              <table className="qp-table">
                <colgroup>
                  <col className="qp-col-desc" />
                  <col className="qp-col-qty" />
                  <col className="qp-col-pu" />
                  <col className="qp-col-vat" />
                  <col className="qp-col-ht" />
                  <col className="qp-col-ttc" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Désignation et description</th>
                    <th className="qp-num">Quantité</th>
                    <th className="qp-num">Prix u. HT</th>
                    <th className="qp-num">TVA</th>
                    <th className="qp-num">Montant HT</th>
                    <th className="qp-num">Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const parts = String(line.description ?? '').replace(/\r\n/g, '\n').split('\n');
                    const lineTitle = parts[0]?.trim() || '';
                    const body = parts.slice(1).join('\n').trim();
                    const ht = Number(line.ht_total);
                    const lineRate = line.vat_rate == null ? vatRate : Number(line.vat_rate);
                    const ttc = ht * (1 + lineRate / 100);
                    return (
                      <tr key={line.id}>
                        <td>
                          <div className="qp-line-title">{lineTitle}</div>
                          {body ? <div className="qp-line-body">{body}</div> : null}
                        </td>
                        <td className="qp-num">{Number(line.quantity).toLocaleString('fr-FR')}</td>
                        <td className="qp-num">{formatEuros(line.unit_price)}</td>
                        <td className="qp-num">{Number.isInteger(lineRate) ? `${lineRate}%` : `${lineRate.toFixed(1).replace('.', ',')}%`}</td>
                        <td className="qp-num">{formatEuros(ht)}</td>
                        <td className="qp-num">{formatEuros(ttc)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={`qp-after${kind === 'credit' ? ' qp-after-credit' : ''}`}>
              {kind === 'credit' ? (
                present(doc.notes) ? <p className="qp-notes">{doc.notes}</p> : <div />
              ) : (
                <div className="qp-pay">
                  {present(doc.notes) ? <p className="qp-notes">{doc.notes}</p> : null}
                  <h2>Conditions de paiement</h2>
                  {terms.map((term) => (
                    <div key={term.label} className="qp-pay-item">
                      <div className="qp-pay-label">{term.label}</div>
                      <div className="qp-pay-value">{term.value}</div>
                    </div>
                  ))}
                  {iban || bic ? (
                    <div className="qp-rib">
                      <div className="qp-rib-title">Relevé d'identité Bancaire</div>
                      {iban ? (
                        <div className="qp-rib-row"><span>IBAN</span><strong>{iban}</strong></div>
                      ) : null}
                      {bic ? (
                        <div className="qp-rib-row"><span>BIC</span><strong>{bic}</strong></div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
              <div className="qp-sign-col">
                <div className="qp-totals">
                  <div><span>Total HT</span><span>{formatEuros(doc.ht_total)}</span></div>
                  {byRate.length > 1 ? (
                    byRate.map((r) => (
                      <div key={r.rate} className="qp-dont"><span>Dont TVA {formatVatRate(r.rate)}</span><span>{formatEuros(r.vat)}</span></div>
                    ))
                  ) : (
                    <div className="qp-dont"><span>Dont TVA {formatVatRate(byRate[0]?.rate ?? vatRate)}</span><span>{formatEuros(doc.vat_total)}</span></div>
                  )}
                  <div className="qp-ttc"><span>Total TTC</span><span>{formatEuros(doc.ttc_total)}</span></div>
                </div>
                {kind === 'quote' ? (
                  <div className="qp-sign-print">
                    <strong>Bon pour accord</strong>
                    <span>Signature du client</span>
                  </div>
                ) : null}
              </div>
            </div>
          </article>
        </div>
      </div>
    </aside>
  );
}
